import { useState } from 'react'
import { useAuth } from './contexts/AuthContext'
import { Login } from './components/Login'
import { UserManager } from './components/UserManager'
import { Dashboard } from './components/Dashboard'
import { InterviewsManager } from './components/InterviewsManager'
import { BillingPage } from './components/BillingPage'
import { PatientsPage } from './components/PatientsPage'
import { SettingsPage } from './components/SettingsPage'
import { DocumentsExplorer } from './components/DocumentsExplorer'
import { SharedFiles } from './components/SharedFiles'
import GlobalAiCopilot from './components/GlobalAiCopilot'
import { NotificationCenter } from './components/NotificationCenter'
import {
  LogOut, Heart, Users, ClipboardList, HeartPulse,
  MapPin, Settings, Loader2, Shield, ShieldCheck, Eye, UserCheck, ShieldAlert, Euro, FolderOpen
} from 'lucide-react'
import type { UserRole } from './lib/types'

type Tab = 'dashboard' | 'interviews' | 'billing' | 'patients' | 'documents' | 'shared_files' | 'users' | 'settings'

const ROLE_BADGE: Record<UserRole, { label: string; color: string; icon: typeof Shield }> = {
  staff: { label: 'Staff', color: 'bg-slate-100 text-slate-600', icon: Eye },
  staff_plus: { label: 'Staff+', color: 'bg-blue-100 text-blue-700', icon: UserCheck },
  admin: { label: 'Admin', color: 'bg-indigo-100 text-indigo-700', icon: Shield },
  admin_plus: { label: 'Admin+', color: 'bg-purple-100 text-purple-700', icon: ShieldAlert },
  super_admin: { label: 'SA', color: 'bg-amber-100 text-amber-700', icon: ShieldCheck },
}

function App() {
  const { user, isLoading, logout, canViewInterviews, canManageUsers, isSuperAdmin } = useAuth()
  const [tab, setTab] = useState<Tab>('dashboard')

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-lg font-medium">Caricamento...</span>
        </div>
      </div>
    )
  }

  // Not logged in
  if (!user) {
    return <Login />
  }

  const badge = ROLE_BADGE[user.role] || ROLE_BADGE.staff
  const BadgeIcon = badge.icon

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50 pb-20 md:pb-4">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl shadow-sm border-b border-slate-200/60 sticky top-0 z-50">
        <div className="w-full px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Logo */}
            <div className="flex items-center gap-3 group">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg shadow-indigo-500/20 group-hover:scale-105 transition-transform">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-black text-slate-800 tracking-tight">
                  Gestione Infermieri
                </h1>
                <span className="text-[10px] text-slate-400 font-medium">v1.0</span>
              </div>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex bg-slate-100/80 p-1 rounded-xl gap-1">
              <NavButton
                active={tab === 'dashboard'}
                onClick={() => setTab('dashboard')}
                icon={<MapPin className="w-4 h-4" />}
                label="Archivio"
                activeColor="bg-indigo-600"
              />
              {canViewInterviews && (
                <NavButton
                  active={tab === 'interviews'}
                  onClick={() => setTab('interviews')}
                  icon={<ClipboardList className="w-4 h-4" />}
                  label="Colloqui"
                  activeColor="bg-emerald-600"
                />
              )}
              {canViewInterviews && (
                <NavButton
                  active={tab === 'billing'}
                  onClick={() => setTab('billing')}
                  icon={<Euro className="w-4 h-4" />}
                  label="Fatturazione"
                  activeColor="bg-amber-600"
                />
              )}
              {canViewInterviews && (
                <NavButton
                  active={tab === 'patients'}
                  onClick={() => setTab('patients')}
                  icon={<HeartPulse className="w-4 h-4" />}
                  label="Pazienti"
                  activeColor="bg-rose-600"
                />
              )}
              {canViewInterviews && (
                <NavButton
                  active={tab === 'documents'}
                  onClick={() => setTab('documents')}
                  icon={<ClipboardList className="w-4 h-4" />}
                  label="Archivio File"
                  activeColor="bg-sky-600"
                />
              )}
              <NavButton
                active={tab === 'shared_files'}
                onClick={() => setTab('shared_files')}
                icon={<FolderOpen className="w-4 h-4" />}
                label="File Progetto"
                activeColor="bg-teal-600"
              />
              {canManageUsers && (
                <NavButton
                  active={tab === 'users'}
                  onClick={() => setTab('users')}
                  icon={<Users className="w-4 h-4" />}
                  label="Utenti"
                  activeColor="bg-pink-600"
                />
              )}
              {isSuperAdmin && (
                <NavButton
                  active={tab === 'settings'}
                  onClick={() => setTab('settings')}
                  icon={<Settings className="w-4 h-4" />}
                  label="Impostazioni"
                  activeColor="bg-slate-600"
                />
              )}
            </nav>
          </div>

          {/* User & logout */}
          <div className="flex items-center gap-3">
            <NotificationCenter />
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl">
              <div className="w-7 h-7 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                {user.displayName.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-bold text-slate-700 hidden sm:block">
                {user.displayName}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 ${badge.color} rounded-md font-bold flex items-center gap-1`}>
                <BadgeIcon className="w-3 h-3" /> {badge.label}
              </span>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors"
              title="Esci"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 flex justify-around p-2 pb-safe-area shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <MobileNavButton
          active={tab === 'dashboard'}
          onClick={() => setTab('dashboard')}
          icon={<MapPin className="w-5 h-5" />}
          label="Archivio"
        />
        {canViewInterviews && (
          <MobileNavButton
            active={tab === 'interviews'}
            onClick={() => setTab('interviews')}
            icon={<ClipboardList className="w-5 h-5" />}
            label="Colloqui"
          />
        )}
        {canViewInterviews && (
          <MobileNavButton
            active={tab === 'billing'}
            onClick={() => setTab('billing')}
            icon={<Euro className="w-5 h-5" />}
            label="Fatturaz."
          />
        )}
        {canViewInterviews && (
          <MobileNavButton
            active={tab === 'patients'}
            onClick={() => setTab('patients')}
            icon={<HeartPulse className="w-5 h-5" />}
            label="Pazienti"
          />
        )}
        {canViewInterviews && (
          <MobileNavButton
            active={tab === 'documents'}
            onClick={() => setTab('documents')}
            icon={<ClipboardList className="w-5 h-5" />}
            label="File"
          />
        )}
        <MobileNavButton
          active={tab === 'shared_files'}
          onClick={() => setTab('shared_files')}
          icon={<FolderOpen className="w-5 h-5" />}
          label="Progetto"
        />
        {canManageUsers && (
          <MobileNavButton
            active={tab === 'users'}
            onClick={() => setTab('users')}
            icon={<Users className="w-5 h-5" />}
            label="Utenti"
          />
        )}
        {isSuperAdmin && (
          <MobileNavButton
            active={tab === 'settings'}
            onClick={() => setTab('settings')}
            icon={<Settings className="w-5 h-5" />}
            label="Impostazioni"
          />
        )}
      </nav>

      {/* Main content */}
      <main className="w-full p-4 md:px-8">
        {tab === 'dashboard' && <Dashboard />}
        {tab === 'interviews' && canViewInterviews && <InterviewsManager />}
        {tab === 'billing' && canViewInterviews && <BillingPage />}
        {tab === 'patients' && canViewInterviews && <PatientsPage />}
        {tab === 'documents' && canViewInterviews && <DocumentsExplorer />}
        {tab === 'shared_files' && <SharedFiles />}
        {tab === 'users' && canManageUsers && <UserManager />}
        {tab === 'settings' && isSuperAdmin && <SettingsPage />}
      </main>

      <GlobalAiCopilot />
    </div>
  )
}

// Desktop nav button
function NavButton({ active, onClick, icon, label, activeColor }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; activeColor: string
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm font-bold rounded-lg transition-all flex items-center gap-1.5 ${
        active
          ? `${activeColor} text-white shadow-md`
          : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'
      }`}
    >
      {icon} {label}
    </button>
  )
}

// Mobile nav button
function MobileNavButton({ active, onClick, icon, label }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all w-full ${
        active ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400'
      }`}
    >
      {icon}
      <span className="text-[10px] font-bold">{label}</span>
    </button>
  )
}

export default App
